import { motion } from 'framer-motion';
import { Search, Plus, MoreVertical, Filter, ArrowUpDown, CheckCircle2 } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';

const statusTabs = [
  { label: 'All', count: 125, active: true },
  { label: 'Active', count: 17 },
  { label: 'Inactive', count: 24 },
  { label: 'Pending', count: 26 },
];

const users = [
  { name: 'Ahmed Ali',         facility: 'Abu dhabi hospital', others: 3, status: 'Active',   dateAdded: "15 Nov' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "17 Nov' 25" },
  { name: 'Fatima Al Ali',     facility: 'Abu dhabi hospital', others: 3, status: 'Active',   dateAdded: "5 Nov' 25",   lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "10 Nov' 25" },
  { name: 'Aisha Al Mansoori', facility: 'Abu dhabi hospital', others: 3, status: 'Inactive', dateAdded: "26 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "30 Oct' 25" },
  { name: 'Khalid Al Ameri',   facility: 'Abu dhabi hospital', others: 3, status: 'Pending',  dateAdded: "15 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "19 Oct' 25" },
  { name: 'Sara Al Mulla',     facility: 'Abu dhabi hospital', others: 3, status: 'Active',   dateAdded: "10 Oct' 25",  lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "12 Oct' 25" },
  { name: 'Nasser Al Neyadi',  facility: 'Abu dhabi hospital', others: 3, status: 'Active',   dateAdded: "2 Oct' 25",   lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "10 Oct' 25" },
  { name: 'Amna Al Mazrouei',  facility: 'Abu dhabi hospital', others: 3, status: 'Pending',  dateAdded: "28 Sept' 25", lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "1 Oct' 25" },
  { name: 'Tariq Al Mutawa',   facility: 'Abu dhabi hospital', others: 3, status: 'Active',   dateAdded: "21 Sept' 25", lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "15 Oct' 25" },
  { name: 'Layla Al Hosani',   facility: 'Abu dhabi hospital', others: 3, status: 'Active',   dateAdded: "15 Sept' 25", lastLogin: "10 Oct' 25", lastLoginTime: '11:00 AM', lastModified: "10 Oct' 25" },
];

function StatusPill({ status }) {
  const styles = {
    Active:   'bg-emerald-50 text-emerald-700 border-emerald-200',
    Inactive: 'bg-gray-100 text-gray-600 border-gray-200',
    Pending:  'bg-amber-50 text-amber-700 border-amber-200',
  };
  return <span className={`inline-block px-2.5 py-0.5 rounded-md text-[11px] font-medium border ${styles[status]}`}>{status}</span>;
}

export default function UsersListV2Scene({ onNext, showSuccessToast = false }) {
  const cursorPath = [
    { x: 260, y: 60,  delay: 1.0 },                    // title hover
    { x: 260, y: 60,  delay: 2.2 },                    // linger for InfoSpot
    { x: 1040, y: 85, delay: 1.8 },                    // move to Add new user
    { x: 1040, y: 85, delay: 1.5, click: true },       // click
  ];

  return (
    <PortalShellV2
      variant="sidebar"
      menu="full"
      activeGroup="Manage"
      activeSub="Users"
      overlay={<AutoCursor targets={cursorPath} startDelay={0.4} />}
    >
      <div data-testid="scene-users-list-v2" className="px-8 pt-6 pb-4 relative">
        {/* Success toast */}
        {showSuccessToast && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-3 left-1/2 -translate-x-1/2 z-30 px-4 py-2.5 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2.5 shadow-sm"
          >
            <CheckCircle2 size={16} className="text-emerald-600" />
            <p className="text-[13px] font-body text-emerald-800">
              We've sent <span className="font-semibold">Abdul Ahmed</span> a sign-in link. Once they confirm their details, you'll be able to continue.
            </p>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={showSuccessToast ? 'mt-10' : ''}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="font-heading font-extrabold text-[34px] leading-none text-[#0A0A0A] flex items-center gap-2">
                Users
                {!showSuccessToast && (
                  <InfoSpot
                    text="The Users page shows all users in your organization. Admins can manage roles, view activity, and add new users."
                    position="bottom"
                    autoOpenAt={1.0}
                    autoCloseAfter={2.2}
                    glowAt={0.7}
                  />
                )}
              </h1>
              <p className="mt-2 font-body text-[13px] text-[#6B7280]">Manage the users and their roles &amp; permissions in your organization</p>
            </div>
            <motion.button
              data-testid="add-new-user-v2-button"
              onClick={onNext}
              animate={showSuccessToast ? {} : {
                scale: [1, 1, 1.08, 0.97, 1.05, 1],
                boxShadow: [
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 8px rgba(13,148,136,0.35)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 10px rgba(13,148,136,0.3)',
                  '0 0 0 0 rgba(13,148,136,0)',
                ],
              }}
              transition={{ duration: 3.8, delay: 3.6, times: [0, 0.3, 0.42, 0.55, 0.7, 1] }}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-teal-600 text-white text-[13px] font-body font-semibold hover:bg-teal-700 transition-colors cursor-pointer"
            >
              <Plus size={15} /> Add new user
            </motion.button>
          </div>

          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-0 rounded-xl bg-white border border-[#E5E7EB] p-1">
              {statusTabs.map(tab => (
                <div key={tab.label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-body cursor-pointer transition-colors ${tab.active ? 'bg-white text-[#0A0A0A] font-semibold shadow-sm border border-[#E5E7EB]' : 'text-[#6B7280]'}`}>
                  {tab.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab.active ? 'bg-teal-600 text-white' : 'bg-[#E5E7EB] text-[#6B7280]'}`}>{tab.count}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center"><Search size={14} className="text-[#6B7280]" /></button>
              <button className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center"><Filter size={14} className="text-[#6B7280]" /></button>
            </div>
          </div>
        </motion.div>

        <div className="rounded-xl border border-[#E5E7EB] overflow-hidden bg-white">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
                {['User name', 'Facility', 'Status', 'Date added', 'Last login', 'Last modified'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-[12px] font-body font-semibold text-[#374151]">
                    <span className="inline-flex items-center gap-1">{h}<ArrowUpDown size={11} className="text-[#9CA3AF]" /></span>
                  </th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <motion.tr
                  key={u.name}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.04 }}
                  className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFAFA]/60"
                >
                  <td className="px-5 py-3 text-[13px] font-body font-medium text-[#0A0A0A]">{u.name}</td>
                  <td className="px-5 py-3 text-[13px] font-body text-[#374151]">
                    {u.facility} <span className="text-teal-600 text-[11px]">+ {u.others} others</span>
                  </td>
                  <td className="px-5 py-3"><StatusPill status={u.status} /></td>
                  <td className="px-5 py-3 text-[13px] font-body text-[#374151]">{u.dateAdded}</td>
                  <td className="px-5 py-3">
                    <p className="text-[13px] font-body text-[#374151]">{u.lastLogin}</p>
                    <p className="text-[11px] font-body text-[#9CA3AF]">{u.lastLoginTime}</p>
                  </td>
                  <td className="px-5 py-3 text-[13px] font-body text-[#374151]">{u.lastModified}</td>
                  <td className="px-3 py-3"><MoreVertical size={14} className="text-[#9CA3AF]" /></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-5 py-3 bg-white">
            <span className="text-[12px] font-body text-[#6B7280]">Page 1 of 5</span>
            <div className="flex items-center gap-1">
              <button className="px-2.5 py-1 text-[12px] font-body text-[#6B7280]">&lt; Previous</button>
              <button className="w-7 h-7 rounded-md bg-white border border-[#E5E7EB] text-[12px] font-body text-[#0A0A0A]">1</button>
              <button className="w-7 h-7 rounded-md bg-teal-600 text-white text-[12px] font-body font-medium">2</button>
              <button className="w-7 h-7 rounded-md text-[12px] font-body text-[#6B7280]">3</button>
              <span className="text-[12px] font-body text-[#9CA3AF]">…</span>
              <button className="px-2.5 py-1 text-[12px] font-body text-[#6B7280]">Next &gt;</button>
            </div>
          </div>
        </div>
      </div>
    </PortalShellV2>
  );
}
